import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminTestimoniesPage from '../components/admin/AdminTestimoniesPage';
import AdminAcademyPage from '../components/admin/AdminAcademyPage';
import AdminClinicPage from '../components/admin/AdminClinicPage';
import AdminRehabPage from '../components/admin/AdminRehabPage';
import AdminActsFellowshipPage from '../components/admin/AdminActsFellowshipPage';
import AdminResourcesPage from '../components/admin/AdminResourcesPage';
import AdminDonatePage from '../components/admin/AdminDonatePage';
import AdminHomePage from '../components/admin/AdminHomePage';
import AdminAboutPage from '../components/admin/AdminAboutPage';
import AdminBuildingProjectPage from '../components/admin/AdminBuildingProjectPage';
import AdminActivitiesPage from '../components/admin/AdminActivitiesPage';
import AdminGalleryPage from '../components/admin/AdminGalleryPage';
import AdminContactPage from '../components/admin/AdminContactPage';
import {
  AdminPageWrapper,
  AdminShell,
  AdminSidebar,
  AdminBrand,
  AdminNav,
  AdminNavLink,
  AdminSidebarFooter,
  AdminMain,
  AdminHeader,
  AdminHeaderTitle,
  AdminHeaderActions,
  AdminHeaderUser,
  AdminButton,
  AdminLinkButton,
  AdminContent,
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginWrapper,
  AdminLoginCard,
  AdminLoginTitle,
  AdminLoginField,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginError,
  AdminLoginActions,
  AdminLoginButton,
  AdminSecondaryLink,
} from '../styles/AdminPageStyles';

const AdminLogin = () => {
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({ username: '', password: '' });
  const [localError, setLocalError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!formValues.username || !formValues.password) {
      setLocalError('Please enter both username and password.');
      return;
    }

    const result = await login(formValues);
    if (result && result.success) {
      navigate('/admin', { replace: true });
    }
  };

  return (
    <AdminLoginWrapper>
      <AdminLoginCard>
        <AdminLoginTitle>Admin Login</AdminLoginTitle>

        {(localError || error) && (
          <AdminLoginError>{localError || error}</AdminLoginError>
        )}

        <form onSubmit={handleSubmit}>
          <AdminLoginField>
            <AdminLoginLabel htmlFor="username">Username</AdminLoginLabel>
            <AdminLoginInput
              id="username"
              name="username"
              value={formValues.username}
              onChange={handleChange}
              autoComplete="username"
            />
          </AdminLoginField>

          <AdminLoginField>
            <AdminLoginLabel htmlFor="password">Password</AdminLoginLabel>
            <AdminLoginInput
              id="password"
              type="password"
              name="password"
              value={formValues.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
          </AdminLoginField>

          <AdminLoginActions>
            <AdminLoginButton type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </AdminLoginButton>
            <AdminSecondaryLink href="/">Back to Website</AdminSecondaryLink>
          </AdminLoginActions>
        </form>
      </AdminLoginCard>
    </AdminLoginWrapper>
  );
};

const AdminDashboardHome = () => (
  <>
    <AdminSectionTitle>Dashboard Overview</AdminSectionTitle>
    <AdminSectionText>
      Use the navigation on the left to manage each section of the website. You can
      update page content, testimonies, training resources, and program details.
    </AdminSectionText>
  </>
);

const getTitleForPath = (pathname) => {
  if (pathname.startsWith('/admin/home')) return 'Home Page CMS';
  if (pathname.startsWith('/admin/about')) return 'About Page CMS';
  if (pathname.startsWith('/admin/building-project')) return 'Building Project CMS';
  if (pathname.startsWith('/admin/activities')) return 'Church Activities CMS';
  if (pathname.startsWith('/admin/gallery')) return 'Gallery CMS';
  if (pathname.startsWith('/admin/testimonies')) return 'Testimonies CMS';
  if (pathname.startsWith('/admin/contact')) return 'Contact Page CMS';
  if (pathname.startsWith('/admin/academy')) return 'Academy / Refuge Home CMS';
  if (pathname.startsWith('/admin/clinic')) return 'Community Clinic CMS';
  if (pathname.startsWith('/admin/rehab')) return 'Rehab Program CMS';
  if (pathname.startsWith('/admin/acts-fellowship')) return 'Acts Fellowship CMS';
  if (pathname.startsWith('/admin/resources')) return 'Resources / Training CMS';
  if (pathname.startsWith('/admin/donate')) return 'Donate CMS';
  return 'Admin Dashboard';
};

const AdminProtectedShell = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const title = getTitleForPath(location.pathname);

  return (
    <AdminPageWrapper>
      <AdminShell>
        <AdminSidebar>
          <AdminBrand>VFM CMS Admin</AdminBrand>
          <AdminNav>
            <AdminNavLink end to="/admin">
              Dashboard
            </AdminNavLink>
            <AdminNavLink to="/admin/home">Home Page</AdminNavLink>
            <AdminNavLink to="/admin/about">About Page</AdminNavLink>
            <AdminNavLink to="/admin/building-project">Building Project</AdminNavLink>
            <AdminNavLink to="/admin/activities">Activities</AdminNavLink>
            <AdminNavLink to="/admin/gallery">Gallery</AdminNavLink>
            <AdminNavLink to="/admin/testimonies">Testimonies</AdminNavLink>
            <AdminNavLink to="/admin/contact">Contact Page</AdminNavLink>
            <AdminNavLink to="/admin/academy">Academy / Refuge Home</AdminNavLink>
            <AdminNavLink to="/admin/clinic">Community Clinic</AdminNavLink>
            <AdminNavLink to="/admin/rehab">Rehab Program</AdminNavLink>
            <AdminNavLink to="/admin/acts-fellowship">Acts Fellowship</AdminNavLink>
            <AdminNavLink to="/admin/resources">Resources / Training</AdminNavLink>
            <AdminNavLink to="/admin/donate">Donate</AdminNavLink>
          </AdminNav>
          <AdminSidebarFooter>
            <div>Signed in as:</div>
            <div>{user && user.username}</div>
          </AdminSidebarFooter>
        </AdminSidebar>

        <AdminMain>
          <AdminHeader>
            <AdminHeaderTitle>{title}</AdminHeaderTitle>
            <AdminHeaderActions>
              <AdminHeaderUser>
                {user && user.role && (
                  <span>
                    Role:
                    {' '}
                    {user.role}
                  </span>
                )}
              </AdminHeaderUser>
              <AdminLinkButton href="/">Back to Website</AdminLinkButton>
              <AdminButton type="button" onClick={logout}>
                Logout
              </AdminButton>
            </AdminHeaderActions>
          </AdminHeader>

          <AdminContent>
            <Routes>
              <Route path="/" element={<AdminDashboardHome />} />
              <Route path="/home" element={<AdminHomePage />} />
              <Route path="/about" element={<AdminAboutPage />} />
              <Route path="/building-project" element={<AdminBuildingProjectPage />} />
              <Route path="/activities" element={<AdminActivitiesPage />} />
              <Route path="/gallery" element={<AdminGalleryPage />} />
              <Route path="/testimonies" element={<AdminTestimoniesPage />} />
              <Route path="/contact" element={<AdminContactPage />} />
              <Route path="/academy" element={<AdminAcademyPage />} />
              <Route path="/clinic" element={<AdminClinicPage />} />
              <Route path="/rehab" element={<AdminRehabPage />} />
              <Route path="/acts-fellowship" element={<AdminActsFellowshipPage />} />
              <Route path="/resources" element={<AdminResourcesPage />} />
              <Route path="/donate" element={<AdminDonatePage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </AdminContent>
        </AdminMain>
      </AdminShell>
    </AdminPageWrapper>
  );
};

const AdminPage = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/admin" replace /> : <AdminLogin />}
      />
      <Route
        path="/*"
        element={isAuthenticated ? <AdminProtectedShell /> : <Navigate to="/admin/login" replace />}
      />
    </Routes>
  );
};

export default AdminPage;
